/**
 * On-screen driving controls.
 *
 * Two hold-to-steer arrows on the left, an accelerator and a brake/reverse
 * pedal on the right, plus the contextual refuel button. The buttons feed the
 * same virtual action set as the keyboard, so touch, mouse and keys can be
 * mixed freely. Every pointer is tracked independently: one thumb can hold the
 * accelerator while another steers.
 *
 * The controls live inside the HUD, which is `pointer-events: none`, so each
 * button re-enables pointer events. Presses are claimed by pointer id and
 * released from a window listener, which keeps a hold alive if the finger
 * slides off the button.
 */
export class TouchControls {
  constructor({ input }) {
    this.input = input;
    this.container = document.getElementById('hud-controls');
    this.buttons = Array.from(
      this.container?.querySelectorAll('[data-action]') ?? [],
    );

    /** pointerId -> { button, action } for every pointer currently holding. */
    this._active = new Map();
    this._disposed = false;

    this._onPointerDown = (event) => this.#press(event);
    this._onPointerUp = (event) => this.#release(event);
    this._onPointerCancel = (event) => this.#release(event);
    this._onContextMenu = (event) => {
      if (event.target.closest?.('[data-action]')) event.preventDefault();
    };
    this._onDragStart = (event) => event.preventDefault();
    this._onBlur = () => this.releaseAll();

    if (this.container) {
      this.container.addEventListener('pointerdown', this._onPointerDown);
      this.container.addEventListener('contextmenu', this._onContextMenu);
      this.container.addEventListener('dragstart', this._onDragStart);
    }
    window.addEventListener('pointerup', this._onPointerUp);
    window.addEventListener('pointercancel', this._onPointerCancel);
    window.addEventListener('blur', this._onBlur);

    // Pointer capture normally releases on pointerup. If the browser takes it
    // away early, clear the hold rather than leaving a pedal stuck down.
    for (const button of this.buttons) {
      button.addEventListener('lostpointercapture', this._onPointerCancel);
    }
  }

  #press(event) {
    if (this._disposed || event.button !== 0) return;

    const button = event.target.closest?.('[data-action]');
    if (!button || !this.buttons.includes(button)) return;
    if (this._active.has(event.pointerId)) return;

    event.preventDefault();
    event.stopPropagation();

    const action = button.dataset.action;
    this._active.set(event.pointerId, { button, action });
    button.classList.add('is-pressed');

    // Capture is a nicety: a synthetic pointer or an already-released pointer
    // can throw here without invalidating the press.
    try {
      button.setPointerCapture?.(event.pointerId);
    } catch {
      /* the window pointer listeners still release the hold */
    }

    this.input.setVirtual(action, true);
  }

  #release(event) {
    const active = this._active.get(event.pointerId);
    if (!active) return;

    this._active.delete(event.pointerId);

    // Another finger may be holding the same action (or the same button). Only
    // release the action once every pointer has let go.
    const stillHeld = Array.from(this._active.values()).some(
      (entry) => entry.action === active.action,
    );
    const buttonStillHeld = Array.from(this._active.values()).some(
      (entry) => entry.button === active.button,
    );

    if (!buttonStillHeld) {
      active.button.classList.remove('is-pressed');
      // A focused control would make the keyboard ignore Space/arrow keys.
      if (document.activeElement === active.button) active.button.blur();
    }
    if (!stillHeld) this.input.setVirtual(active.action, false);
  }

  /** Drop every hold, e.g. when the tab loses focus mid-press. */
  releaseAll() {
    if (this._active.size === 0) return;

    const actions = new Set();
    for (const { button, action } of this._active.values()) {
      button.classList.remove('is-pressed');
      actions.add(action);
    }
    this._active.clear();

    for (const action of actions) this.input.setVirtual(action, false);
  }

  dispose() {
    if (this._disposed) return;
    this._disposed = true;
    this.releaseAll();

    if (this.container) {
      this.container.removeEventListener('pointerdown', this._onPointerDown);
      this.container.removeEventListener('contextmenu', this._onContextMenu);
      this.container.removeEventListener('dragstart', this._onDragStart);
    }
    window.removeEventListener('pointerup', this._onPointerUp);
    window.removeEventListener('pointercancel', this._onPointerCancel);
    window.removeEventListener('blur', this._onBlur);

    for (const button of this.buttons) {
      button.removeEventListener('lostpointercapture', this._onPointerCancel);
    }
  }
}
